
export enum WinThresholds {
    ONE = 0,
    TWO = 0.25,
    THREE = 0.5,
    FOUR = 0.75,
    FIVE = 1,
    SIX = 1.25,
    SEVEN = 1.5,
    EIGHT = 1.75,
    NINE = 2,
    TEN = 5,
    ELEVEN = 8,
    TWELVE = 12,
    THIRTEEN = 14,
    FOURTEEN = 16,
    FIFTEEN = 20,
    SIXTEEN = 50
}

export enum BigWinThresholds {
    // Tiny's don't animate.
    TINY,

    // Symbol Animations play.
    REGULAR = 1,

    // Small wins animate particles around the win meter.
    SMALL = 10,

    // Big particle celebration.
    BIG = 20,

    // Huge particle celebration.
    HUGE = 50
}

class WinMagnitude {
    protected freespinSoundDurations = {
        min: 2500,
        max: 6000
    };

    protected winThresholds = [
        { threshold: WinThresholds.ONE, audioDuration: 361.405895691604 },
        { threshold: WinThresholds.TWO, audioDuration: 596.462585034032 },
        { threshold: WinThresholds.THREE, audioDuration: 429.569160997743 },
        { threshold: WinThresholds.FOUR, audioDuration: 1383.76417233558 },
        { threshold: WinThresholds.FIVE, audioDuration: 715.827664399114 },
        { threshold: WinThresholds.SIX, audioDuration: 1758.91156462586 },
        { threshold: WinThresholds.SEVEN, audioDuration: 2178.32199546484 },
        { threshold: WinThresholds.EIGHT, audioDuration: 3250.79365079363 },
        { threshold: WinThresholds.NINE, audioDuration: 3644.08163265306 },
        { threshold: WinThresholds.TEN, audioDuration: 4051.88208616777 },
        { threshold: WinThresholds.ELEVEN, audioDuration: 4568.52607709748 },
        { threshold: WinThresholds.TWELVE, audioDuration: 5427.66439909297 },
        { threshold: WinThresholds.THIRTEEN, audioDuration: 6849.88662131519 },
        { threshold: WinThresholds.FOURTEEN, audioDuration: 8235.82766439909 },
        { threshold: WinThresholds.FIFTEEN, audioDuration: 8785.85034013605 },
        { threshold: WinThresholds.SIXTEEN, audioDuration: 49003 }
    ];

    public getWinLevel(winRatio: number): number {
        return this.getWinLevelIndex(winRatio) + 1;
    }

    public getBigWinLevel(winRatio: number): BigWinThresholds {
        if (winRatio <= BigWinThresholds.REGULAR) {
            return BigWinThresholds.TINY;
        }

        if (winRatio >= BigWinThresholds.HUGE) {
            return BigWinThresholds.HUGE;
        }

        if (winRatio >= BigWinThresholds.BIG) {
            return BigWinThresholds.BIG;
        }

        if (winRatio >= BigWinThresholds.SMALL) {
            return BigWinThresholds.SMALL;
        }

        return BigWinThresholds.REGULAR;
    }

    public getWinLevelIndex(winRatio: number): number {
        const winLevelValue = this.winThresholds.find((winLevel, index) => {
            const numberOfThresholds = this.winThresholds.length;
            const higherThanPreviousThreshold = index === 0 || winRatio > this.winThresholds[index - 1].threshold;
            const lessThanNextThreshold = index >= numberOfThresholds - 1 || winRatio < this.winThresholds[index + 1].threshold;

            if (higherThanPreviousThreshold && lessThanNextThreshold) {
                return true;
            }
        });

        return this.winThresholds.indexOf(winLevelValue);
    }

    public getSoundAlias(winRatio: number): string {
        let winLevel = this.getWinLevel(winRatio);

        if (winLevel > 15) {
            winLevel = 15;
        }

        return `rollup_level${winLevel}`;
    }

    public getSoundDuration(winRatio: number, isBonus: boolean): number {
        if (isBonus) {
            return this.getFreepinSoundDuration(winRatio);
        } else {
            return this.getBaseSoundDuration(winRatio);
        }
    }

    public getBaseSoundDuration(winRatio: number): number {
        return this.getThresholdData(winRatio).audioDuration;
    }

    public getFreepinSoundDuration(winRatio: number): number {
        const baseDuration = this.freespinSoundDurations.min;
        const durationDifference = this.freespinSoundDurations.max - this.freespinSoundDurations.min;
        const winLevel = this.getWinLevel(winRatio);
        const winlevelRatio = (winLevel - 1) / this.winThresholds.length;
        return baseDuration + winlevelRatio * durationDifference;
    }

    protected getThresholdData(winRatio: number): { threshold: WinThresholds, audioDuration: number} {
        return this.winThresholds[this.getWinLevelIndex(winRatio)];
    }
}

export const winMagnitude = new WinMagnitude();
